import ImageEditor from "@Components/ImageEditor";
import GoogleMap from "@Components/Map";
import { evaluateFormula, optionsViewType } from "@Features/portal-settings";
import { PlusIcon } from "@Components/StickySidebarViewer/assets/selectionIcons";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import IconComponent from "@CubeComponents/icons";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import DateTimeComponent from "@CubeComponents/Mui_Form/DateTimeComponent";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { getDistanceInMeters, SECTION_TYPE_TABLE_SECTION, DEFAULT_IMAGE_DISPLAY_WIDTH } from "@Helpers/constants";
import { t } from "@i18n";
import { Box, CircularProgress, FormControlLabel, FormGroup, Link, RadioGroup } from "@mui/material";
import { convertDate } from "@Services/luxonHelper";
import TreeSelectDropDown from "@CubeComponents/html/TreeDropDown";
import { getBackendUrl } from "@Utils";
import _ from "lodash";
import { formSectionType, getUrlFromField, buildConcatenatedValue, toFiniteNumber } from "./constants";
import UserCard from "./userCard";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import { getFileIcon } from "@CubeComponents/constants/globalFunctions";
import Text from "@CubeComponents/html/Text/Text";
import { getViewerUrl } from "@Helpers/urlHelpers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { TemplateQuestionField, TemplateSection } from "@Helpers/types/layout/types";
import type { ReactNode } from "react";

type RenderableQuestionField = Omit<TemplateQuestionField, "displayAs"> & {
  defaultValue?: any;
  displaySize?: number;
  displayAs?: TemplateQuestionField["displayAs"] | "dateAndTime";
};

type FieldAnswer = {
  value?: any;
  createdBy?: string;
  createdDate?: number;
  [key: string]: any;
};

type QuestionFieldRenderProps = {
  eachField: RenderableQuestionField;
  currentAnswer: FieldAnswer;
  enabled: boolean;
  name: string;
  source: string;
  width?: string;
  formValidator: Record<string, any>;
  section: Partial<TemplateSection>;
  fieldIndex: number | null;
  sectionIndex: number | null;
  sectionId: string;
  questionId: string;
  tableRowIndex: number;
  tableRowId: string | number | null;
  formData: { selectedForm?: { template?: TemplateSection[] }; [key: string]: any };
  extraParams: Record<string, any>;
  spaceId?: string;
  projectId?: string;
  multiTextRows?: number;
  locationData: Array<{ id: string; label: string; parentId?: string | null; [key: string]: any }>;
  teamsData: Array<{ id: string; name: string }>;
  isFileUploading: Array<{ type?: string; uniqueId: string | number; [key: string]: any }>;
  handleCustomFieldData: ((args: any) => void) | null;
  handleOnChange: (args?: any) => void;
  handleOnBlur: (args?: any) => void;
  handleRemoveLocation: () => void;
  handleFileUploadClick: () => void;
  handleUrlFieldClick: () => void;
  handleFileUploadChange: (args: any) => void;
  handleRemoveUploadedFile: (args: any) => void;
  handleURLModalState: (args: any) => void;
  handleGeoLocationModalState: (args: any) => void;
  removeUserFromList: (args: any) => void;
  openAddUserModal: (args: any) => void;
  saveEditedImage: (args: any) => void;
};

export const getMapWarnings = (warningMessage = "") => (
  <div className={`h-12 bg-system_colour_12 w-fit ps-3 gap-2 bg-opacity-30 pe-3 rounded-lg flex items-center`}>
    <span
      className={`w-5 h-5 flex items-center justify-center rounded-full text-white text-xs cursor-default bg-yellow-400`}
    >
      !
    </span>
    <span>{warningMessage}</span>
  </div>
);

export const getOverDistanceWarning = () => getMapWarnings(t("Selected location differs from captured location"));

export const getNoPermissionWarning = () =>
  getMapWarnings(t("Live location not captured since necessary permission not given"));

export const getUserCardAndChip = ({ user, p }: { user: any; p: QuestionFieldRenderProps }) => {
  return p.eachField.viewType === optionsViewType.CHIP ? (
    <UserNameTagComponent
      id={user._id}
      handleDeleteIconClicked={
        p.enabled
          ? () =>
              p.removeUserFromList({
                fieldIndex: p.fieldIndex,
                sectionIndex: p.sectionIndex,
                user,
                eachField: p.eachField,
                section: p.section,
                source: p.source,
                tableRowIndex: p.tableRowIndex,
                handleCustomFieldData: p.handleCustomFieldData,
                extraParams: p.extraParams,
              })
          : null
      }
    />
  ) : (
    <UserCard
      fieldIndex={p.fieldIndex}
      sectionIndex={p.sectionIndex}
      section={p.section}
      source={p.source}
      tableRowIndex={p.tableRowIndex}
      showAnswersOnly={!p.enabled}
      handleCustomFieldData={p.handleCustomFieldData}
      extraParams={p.extraParams}
      removeUserFromList={p.removeUserFromList}
      field={p.eachField}
      id={user._id}
    />
  );
};

export const renderSingleLine = (p: QuestionFieldRenderProps) => (
  <TextFieldComponent
    defaultValue={p.eachField.defaultValue ?? p.currentAnswer.value ?? null}
    onChange={p.handleOnChange}
    onBlur={p.handleOnBlur}
    disabled={!p.enabled}
    size="small"
    key={p.name}
    error={!!_.get(p.formValidator, `error.${p.name}`)}
    placeholder={p.eachField.placeholderText ?? ""}
    width={p.width}
  />
);

export const renderMultiLine = (p: QuestionFieldRenderProps) => (
  <textarea
    style={{
      border: "1px solid #19204880",
      outline: "none",
      width: p.source === formSectionType.FORM ? "80%" : "100%",
      padding: "0.42rem 0 0.42rem 0.5rem",
      height: "100%",
      color: "black",
    }}
    disabled={!p.enabled}
    rows={p.multiTextRows || 5}
    key={p.name}
    name={p.name}
    placeholder={p.eachField.placeholderText ?? ""}
    defaultValue={p.eachField.defaultValue ?? p.currentAnswer.value ?? ""}
    onBlur={p.handleOnBlur}
    onChange={p.handleOnChange}
  />
);

export const renderSingleSelect = (p: QuestionFieldRenderProps) => {
  if (p.eachField.viewType === optionsViewType.LIST) {
    return (
      <RadioGroup
        value={p.currentAnswer.value ?? undefined}
        defaultValue={p.eachField.defaultValue}
        onChange={(args) => {
          p.handleOnBlur(args);
          p.handleOnChange(args);
        }}
      >
        {p.eachField.answerChoices?.map((choice: any) => (
          <FormControlLabel
            key={choice}
            disabled={!p.enabled}
            value={choice}
            control={<RadioButton />}
            label={choice}
          />
        ))}
      </RadioGroup>
    );
  }

  return (
    <AutoComplete
      disabled={!p.enabled}
      value={p.currentAnswer.value ?? undefined}
      defaultValue={p.eachField.defaultValue}
      onChange={(_: any, newValue: any) => {
        p.handleOnChange({ target: { value: newValue } });
        p.handleOnBlur({ target: { value: newValue } });
      }}
      id={p.name}
      options={p.eachField.answerChoices}
      getOptionLabel={(opt: any) => opt}
      sx={{ width: p.width }}
      renderInput={(params: any) => (
        <TextFieldComponent
          {...params}
          placeholder={p.eachField.placeholderText ?? ""}
          error={!!_.get(p.formValidator, `error.${p.name}`)}
        />
      )}
    />
  );
};

export const renderMultiSelect = (p: QuestionFieldRenderProps) => {
  if (p.eachField.viewType === optionsViewType.LIST) {
    return (
      <FormGroup>
        {p.eachField.answerChoices?.map((choice: string) => (
          <FormControlLabel
            key={choice}
            control={
              <CheckBoxComponent
                onChange={(args) => {
                  p.handleOnBlur(args);
                  p.handleOnChange(args);
                }}
                disabled={!p.enabled}
                name={choice}
                checked={Array.isArray(p.currentAnswer.value) && p.currentAnswer.value.includes(choice)}
              />
            }
            label={choice}
          />
        ))}
      </FormGroup>
    );
  }

  return (
    <AutoComplete
      disabled={!p.enabled}
      multiple
      key={p.name}
      disableCloseOnSelect
      value={p.currentAnswer.value ?? undefined}
      defaultValue={p.eachField.defaultValue ?? []}
      getOptionLabel={(opt: any) => opt}
      onChange={(_: any, newValue: any) => {
        p.handleOnChange({ target: { value: newValue } });
        p.handleOnBlur({ target: { value: newValue } });
      }}
      id={p.name}
      options={p.eachField.answerChoices}
      sx={{ width: p.width }}
      renderInput={(params: any) => (
        <TextFieldComponent
          {...params}
          placeholder={p.eachField.placeholderText ?? ""}
          error={!!_.get(p.formValidator, `error.${p.name}`)}
        />
      )}
    />
  );
};

const currentDateForDateField = (p: QuestionFieldRenderProps) =>
  p.enabled ? (
    <Box
      className={`${
        p.currentAnswer?.value ? "cursor-not-allowed pointer-events-none" : "cursor-pointer"
      } border border-slate-400 ${p.source === formSectionType.FORM ? "w-1/2" : "w-full"} h-8 flex items-center p-2`}
      onClick={(args) => {
        p.handleOnBlur(args);
        p.handleOnChange(args);
      }}
    >
      {p.currentAnswer?.value ? (
        convertDate(Number(p.currentAnswer?.value), "dateForTooltip")
      ) : (
        <Text className="text-xs text-slate-500">{t("Click here to set date")}</Text>
      )}
    </Box>
  ) : (
    <div
      className={`cursor-not-allowed pointer-events-none border bg-gray-100 border-slate-400 ${p.source === formSectionType.FORM ? "w-1/2" : "w-full"} h-8 flex items-center p-2`}
    >
      {p.currentAnswer?.value && convertDate(Number(p.currentAnswer?.value), "dateForTooltip")}
    </div>
  );
export const renderDateTime = (p: QuestionFieldRenderProps) => {
  const min = p.eachField.minFieldRange || null;
  const max = p.eachField.maxFieldRange || null;

  let extraProps: any = {};
  if (p.eachField.selectionScope === "pastDateTime") extraProps.disableFuture = true;
  if (p.eachField.selectionScope === "futureDateTime") extraProps.disablePast = !p.currentAnswer.value;
  if (p.eachField.selectionScope === "rangeBased") extraProps = { minDate: min, maxDate: max };

  if (p.eachField.selectionScope === "currentDateTime") {
    return currentDateForDateField(p);
  }

  if (p.eachField.displayAs === "dateAndTime") {
    return (
      <DateTimeComponent
        slotProps={{ field: { clearable: true, onClear: () => p.handleOnBlur(null) } }}
        {...extraProps}
        disabled={!p.enabled}
        key={p.name}
        sx={{ width: p.width }}
        format="DD/MM/YYYY hh:mm A"
        error={!!_.get(p.formValidator, `error.${p.name}`)}
        defaultValue={p.eachField.defaultValue || p.currentAnswer.value || undefined}
        placeholder={p.eachField.placeholderText ?? ""}
        value={p.currentAnswer.value ?? undefined}
        onChange={p.handleOnChange}
        onAccept={p.handleOnBlur}
      />
    );
  } else {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          sx={{
            width: p.width ?? "100%",
            "&.MuiFormControl-root .MuiInputBase-root": {
              height: "2rem",
              background: "white",
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--info-light-blue)",
              borderWidth: "0.0625rem",
            },
          }}
          slotProps={{ field: { clearable: true, onClear: () => p.handleOnBlur(null) } }}
          {...extraProps}
          disabled={!p.enabled}
          format="DD/MM/YYYY"
          error={!!_.get(p.formValidator, `error.${p.name}`)}
          placeholder={p.eachField.placeholderText ?? ""}
          value={p.currentAnswer.value ? dayjs(p.currentAnswer.value) : null}
          onChange={p.handleOnChange}
          onAccept={p.handleOnBlur}
        />
      </LocalizationProvider>
    );
  }
};

const LocationTreeSelect = (p: QuestionFieldRenderProps) => {
  const locationData: any[] = Array.isArray(p.locationData) ? p.locationData : [];

  const selectedLabel = (() => {
    const ids = Array.isArray(p.currentAnswer?.value) ? p.currentAnswer.value.map((a: any) => a.id ?? a) : [];
    const found = locationData.find((d) => ids.includes(d.id));
    return found?.label ?? "";
  })();

  return (
    <div>
      <TreeSelectDropDown
        disabled={!p.enabled}
        key={p.name}
        disabledParentSelection={true}
        placeholder={p.eachField?.placeholderText ?? ""}
        treeData={locationData}
        value={selectedLabel}
        onSelectionChange={(params) => {
          const args = { itemData: { _id: params.id, name: params.label, ...params } };
          p.handleOnChange(args);
          p.handleOnBlur(args);
        }}
      />
    </div>
  );
};

export const renderLookUp = (p: QuestionFieldRenderProps) => {
  if (p.eachField.lookUpAnsFieldType === "Location") {
    return <LocationTreeSelect {...p} />;
  }

  if (p.eachField.lookUpAnsFieldType === "Teams") {
    return (
      <AutoComplete
        disabled={!p.enabled}
        multiple
        key={p.name}
        disableCloseOnSelect
        getOptionLabel={(opt: any) => opt.name}
        value={p.currentAnswer.value ?? []}
        onChange={(_: any, newValue: any) => {
          p.handleOnChange(newValue);
          p.handleOnBlur(newValue);
        }}
        id={p.name}
        options={p.teamsData}
        sx={{ width: p.width }}
        renderInput={(params: any) => (
          <TextFieldComponent
            {...params}
            placeholder={p.eachField.placeholderText ?? ""}
            error={!!_.get(p.formValidator, `error.${p.name}`)}
          />
        )}
      />
    );
  }

  return <></>;
};

export const renderNumber = (p: QuestionFieldRenderProps) => (
  <TextFieldComponent
    defaultValue={p.eachField.defaultValue ?? p.currentAnswer.value ?? ""}
    onChange={p.handleOnChange}
    onBlur={p.handleOnBlur}
    error={!!_.get(p.formValidator, `error.${p.name}`)}
    slotProps={{
      htmlInput: {
        min: p.eachField.minFieldRange ? +p.eachField.minFieldRange : 0,
        max: p.eachField.maxFieldRange ? +p.eachField.maxFieldRange : null,
      },
    }}
    key={p.name}
    disabled={!p.enabled}
    placeholder={p.eachField.placeholderText ?? ""}
    width={p.width}
    type="number"
  />
);

export const renderUsers = (p: QuestionFieldRenderProps) => (
  <div>
    <Box
      onClick={() =>
        p.enabled
          ? p.openAddUserModal({
              eachField: p.eachField,
              fieldIndex: p.fieldIndex,
              sectionIndex: p.sectionIndex,
              section: p.section,
              source: p.source,
              tableRowIndex: p.tableRowIndex,
              handleCustomFieldData: p.handleCustomFieldData,
              extraParams: p.extraParams,
              tableRowId: p.tableRowId,
            })
          : () => {}
      }
      className="bg-button_light h-8 w-14 rounded-3xl justify-center flex cursor-pointer items-center"
    >
      <IconComponent className={p.enabled ? "" : "opacity-50"} name="UserProfile" width="1.5rem" height="1.5rem" />
    </Box>
    <div className="flex flex-wrap gap-5 overflow-auto pt-5">
      {p.currentAnswer.value?.map((user: any) => getUserCardAndChip({ user, p }))}
    </div>
  </div>
);

export const renderFileUpload = (p: QuestionFieldRenderProps) => (
  <div className="flex h-full w-full overflow-auto flex-col gap-5">
    <span>
      <input
        accept={(p.eachField.allowedFileTypes ?? []).join(",")}
        type="file"
        id={`${p.name}`}
        onChange={(e) =>
          p.handleFileUploadChange({
            e,
            sectionId: p.sectionId,
            eachField: p.eachField,
            sectionIndex: p.sectionIndex,
            fieldIndex: p.fieldIndex,
            source: p.source,
            tableRowIndex: p.tableRowIndex,
            tableRowId: p.tableRowId,
            allowedFileTypes: p.eachField.allowedFileTypes,
            formData: p.formData,
            handleCustomFieldData: p.handleCustomFieldData,
            extraParams: p.extraParams,
          })
        }
        style={{ display: "none" }}
      />
      <ButtonComponent
        onClick={p.handleFileUploadClick}
        disabled={!p.enabled}
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
      >
        <span className="flex gap-1 items-center">
          <PlusIcon color={p.enabled ? "var(--primary-text)" : "var(--system-colour-37"} />
          <span>{p.eachField.placeholderText}</span>
        </span>
      </ButtonComponent>
    </span>
    <span className="flex flex-col pe-3 overflow-auto gap-4 w-full h-full">
      {p.currentAnswer.value?.map((data: any, index: number) => (
        <span
          key={`${data.name}-${index}`}
          className="flex gap-3 border-solid border-2 border-button_light w-fit pe-2 rounded-lg ps-2 items-center"
        >
          <IconComponent
            onClick={(e: React.MouseEvent) => handleFileOpen(e, data, p.spaceId, p.projectId)}
            name="openInViewer"
            width="1rem"
            height="1rem"
          />
          <span className="w-14 h-10 items-center flex object-contain"> {getFileIcon(data.name)}</span>
          <span>{data.name}</span>
          {p.enabled && (
            <span className="w-8">
              <IconComponent
                className="cursor-pointer"
                onClick={() =>
                  p.handleRemoveUploadedFile({
                    fileData: data,
                    eachField: p.eachField,
                    sectionIndex: p.sectionIndex,
                    fieldIndex: p.fieldIndex,
                    sectionId: p.sectionId,
                    questionId: p.questionId,
                    tableRowIndex: p.tableRowIndex,
                    source: p.source,
                    handleCustomFieldData: p.handleCustomFieldData,
                    currentAnswer: p.currentAnswer,
                    extraParams: p.extraParams,
                  })
                }
                name="cross"
                width="1rem"
                height="1rem"
              />
            </span>
          )}
        </span>
      ))}
      {p.isFileUploading.map(
        (data: any) =>
          data.uniqueId == p.name && (
            <span
              key={data.uniqueId}
              className="w-32 h-32 border-button_light p-1 items-center justify-center rounded flex border-2"
            >
              <CircularProgress />
            </span>
          ),
      )}
    </span>
  </div>
);

export const renderImage = (p: QuestionFieldRenderProps) => (
  <div className="flex h-full w-full overflow-auto flex-col gap-5">
    <span>
      <input
        accept={(p.eachField.allowedFileTypes ?? []).join(",")}
        type="file"
        id={`${p.name}`}
        onChange={(e) =>
          p.handleFileUploadChange({
            e,
            sectionId: p.sectionId,
            eachField: p.eachField,
            sectionIndex: p.sectionIndex,
            fieldIndex: p.fieldIndex,
            source: p.source,
            tableRowIndex: p.tableRowIndex,
            tableRowId: p.tableRowId,
            allowedFileTypes: p.eachField.allowedFileTypes,
            formData: p.formData,
            handleCustomFieldData: p.handleCustomFieldData,
            extraParams: p.extraParams,
          })
        }
        style={{ display: "none" }}
      />
      <ButtonComponent
        onClick={() => document.getElementById(`${p.name}`)?.click()}
        disabled={!p.enabled}
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
      >
        <span className="flex gap-1 items-center">
          <PlusIcon color={p.enabled ? "var(--primary-text)" : "var(--system-colour-37"} />
          <span>{p.eachField.placeholderText}</span>
        </span>
      </ButtonComponent>
    </span>
    {(p.currentAnswer?.value?.length > 0 || p.isFileUploading?.length > 0) && (
      <span className="flex flex-wrap flex-row pe-3 overflow-auto gap-4 w-full h-full">
        {p.currentAnswer.value?.map((data: any, index: number) => (
          <span
            key={data.awsKey}
            style={{ width: `${p.eachField?.displaySize ?? DEFAULT_IMAGE_DISPLAY_WIDTH}%` }}
            className="h-fit border-button_light p-1 rounded flex border-2"
          >
            <ImageEditor
              isEditable={p.enabled}
              imageData={`${getBackendUrl()}/fileSystemServer/getAttachmentFile/${data.awsKey}`}
              onSubmitFunction={(base64EncodedImg: any) =>
                p.saveEditedImage({
                  eachField: p.eachField,
                  fieldIndex: p.fieldIndex,
                  sectionIndex: p.sectionIndex,
                  tableRowIndex: p.tableRowIndex,
                  source: p.source,
                  imageIndex: index,
                  base64EncodedImg,
                  handleCustomFieldData: p.handleCustomFieldData,
                  currentAnswer: p.currentAnswer,
                  extraParams: p.extraParams,
                  awsKey: data.awsKey,
                  sectionId: p.sectionId,
                  questionId: p.questionId,
                })
              }
              heading={t("Edit Image")}
              onDelete={() =>
                p.handleRemoveUploadedFile({
                  eachField: p.eachField,
                  fileData: data,
                  sectionIndex: p.sectionIndex,
                  fieldIndex: p.fieldIndex,
                  sectionId: p.sectionId,
                  questionId: p.questionId,
                  source: p.source,
                  tableRowIndex: p.tableRowIndex,
                  currentAnswer: p.currentAnswer,
                  handleCustomFieldData: p.handleCustomFieldData,
                })
              }
              defaultImageData={undefined}
            />
          </span>
        ))}
        {p.isFileUploading.map(
          (data: any) =>
            data.uniqueId == p.name && (
              <span
                key={data.uniqueId}
                className="h-32 w-32 border-button_light p-1 items-center justify-center rounded flex border-2"
              >
                <CircularProgress />
              </span>
            ),
        )}
      </span>
    )}
  </div>
);

const getURLEditBtn = (p: QuestionFieldRenderProps) => {
  return (
    <div>
      <ButtonComponent
        disabled={!p.enabled}
        onClick={() => {
          p.handleURLModalState({
            state: true,
            sectionIndex: p.sectionIndex,
            eachField: p.eachField,
            tableRowIndex: p.tableRowIndex,
            answer: p.currentAnswer,
            fieldIndex: p.fieldIndex,
            sectionId: p.sectionId,
            source: p.source,
            tableRowId: p.tableRowId,
          });
        }}
      >
        <IconComponent
          className={`h-5 ${p.enabled ? "" : "opacity-50"}`}
          name="editProfile"
          width="1.25rem"
          height="1.25rem"
        />
      </ButtonComponent>
    </div>
  );
};
export const renderURL = (p: QuestionFieldRenderProps) => {
  if (p.currentAnswer?.value?.displayText && p.currentAnswer?.value?.URL) {
    if (p.eachField.displayAs == "button") {
      return (
        <div className="flex items-center gap-3">
          <ButtonComponent onClick={p.handleUrlFieldClick} buttonType={BUTTON_TYPES.BORDER_BUTTON}>
            <Text sx={{ maxWidth: "12.5rem" }}>{_.get(p.currentAnswer, "value.displayText", "")}</Text>
          </ButtonComponent>
          {getURLEditBtn(p)}
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-3">
          <Box
            onClick={p.handleUrlFieldClick}
            className="text-gradient_color cursor-pointer underline underline-offset-2"
          >
            {_.get(p.currentAnswer, "value.displayText", "")}
          </Box>
          {getURLEditBtn(p)}
        </div>
      );
    }
  }
  return (
    <span>
      <ButtonComponent
        disabled={!p.enabled}
        onClick={() =>
          p.handleURLModalState({
            state: true,
            sectionIndex: p.sectionIndex,
            eachField: p.eachField,
            tableRowIndex: p.tableRowIndex,
            answer: p.currentAnswer,
            fieldIndex: p.fieldIndex,
            sectionId: p.sectionId,
            source: p.source,
            tableRowId: p.tableRowId,
          })
        }
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
      >
        <span className="flex gap-1 items-center">
          <IconComponent name="URL" width="1.5rem" height="1.5rem" className={`h-5 ${p.enabled ? "" : "opacity-50"}`} />
          <Text>{t("Enter URL")}</Text>
        </span>
      </ButtonComponent>
    </span>
  );
};

export const renderGeoLocation = (p: QuestionFieldRenderProps) => {
  const distance = getDistanceInMeters({
    pos1: p.currentAnswer.value?.current,
    pos2: p.currentAnswer.value?.selected,
  });
  return p.currentAnswer.value ? (
    <div className="flex-col flex gap-5">
      <div className="flex h-80 w-120 border flex-col gap-5 border-button_light p-3 rounded-lg">
        <GoogleMap
          allowManualSelection={false}
          allowPanning={false}
          autoFetchLiveLocation={false}
          showLiveLocation={!!p.currentAnswer.value?.current}
          zoomLevel={p.currentAnswer.value?.mapZoomLevel}
          defaultCenter={p.currentAnswer.value?.current}
          additionalMarker={p.currentAnswer.value?.selected}
          allowSearch={false}
          getLocationCallBack={undefined}
        />
        {distance > 500 && getOverDistanceWarning()}
        {p.eachField?.captureLiveLocation && !p.currentAnswer.value?.current && getNoPermissionWarning()}
      </div>
      <div>
        <ButtonComponent disabled={!p.enabled} onClick={p.handleRemoveLocation}>
          <span className="flex gap-1 items-center">
            <IconComponent className={p.enabled ? "" : "opacity-50"} name="delete" width="1.25rem" height="1.25rem" />
            <Text>{t("Remove")}</Text>
          </span>
        </ButtonComponent>
      </div>
    </div>
  ) : (
    <ButtonComponent
      onClick={() =>
        p.handleGeoLocationModalState({
          sectionIndex: p.sectionIndex,
          eachField: p.eachField,
          tableRowIndex: p.tableRowIndex,
          answer: p.currentAnswer,
          fieldIndex: p.fieldIndex,
          sectionId: p.sectionId,
          source: p.source,
          tableRowId: p.tableRowId,
          state: true,
        })
      }
      disabled={!p.enabled}
      buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
    >
      <span className="flex gap-1 items-center">
        <IconComponent className={p.enabled ? "" : "opacity-50"} name="add" width="1rem" height="1rem" />
        <span>{p.eachField.placeholderText}</span>
      </span>
    </ButtonComponent>
  );
};

const renderFormulaField = (p: QuestionFieldRenderProps) => {
  const fieldVariableMapper = p.eachField.fieldsVariableMapper ?? {};
  const formulaExpression = p.eachField.formulaExpression || "";
  const scope: Record<string, number> = {};
  const currentSection = p.section ?? {};
  const isCurrentSectionTable = currentSection.sectionCode === SECTION_TYPE_TABLE_SECTION;

  if (isCurrentSectionTable) {
    const rowData = currentSection.tableDataSource?.[p.tableRowIndex] ?? {};
    (currentSection.questionFields ?? []).forEach((question: TemplateQuestionField) => {
      const fieldKey = fieldVariableMapper[question._id];
      const numericValue = toFiniteNumber(rowData?.[question._id]?.value);
      if (fieldKey && numericValue !== null) {
        scope[fieldKey] = numericValue;
      }
    });
  } else {
    (p.formData?.selectedForm?.template ?? []).forEach((section: TemplateSection) => {
      if (section.sectionCode === SECTION_TYPE_TABLE_SECTION) return;
      (section.questionFields ?? []).forEach((question: TemplateQuestionField) => {
        const fieldKey = fieldVariableMapper[question._id];
        const numericValue = toFiniteNumber(question.answer?.value);
        if (fieldKey && numericValue !== null) {
          scope[fieldKey] = numericValue;
        }
      });
    });
  }

  const calculatedValue = evaluateFormula(formulaExpression, scope);
  return (
    <div className="flex flex-col gap-2" style={{ width: p.width }}>
      <TextFieldComponent value={calculatedValue ?? ""} key={p.name} disabled={true} type="number" />
      {calculatedValue === null && (
        <div className="flex items-center gap-1 text-sm text-slate-500">
          <span className="flex items-center justify-center">
            <IconComponent className="h-5" name="details" width="1.5rem" height="1.5rem" />
          </span>
          {t("This value is calculated automatically once all required fields are filled")}
        </div>
      )}
    </div>
  );
};

const renderConcatenateField = (p: QuestionFieldRenderProps) => {
  const labelById = new Map<string, string>();
  const currentSection = p.section ?? {};
  const isCurrentSectionTable = currentSection.sectionCode === SECTION_TYPE_TABLE_SECTION;
  if (isCurrentSectionTable) {
    const rowData = currentSection.tableDataSource?.[p.tableRowIndex] ?? {};
    Object.keys(rowData).forEach((key: string) => {
      labelById.set(key, rowData[key]?.value ?? "");
    });
  } else {
    (p.formData?.selectedForm?.template ?? []).forEach((section: TemplateSection) => {
      if (section.sectionCode === SECTION_TYPE_TABLE_SECTION) return;
      (section.questionFields ?? []).forEach((question: TemplateQuestionField) => {
        labelById.set(question._id, question?.answer?.value ?? "");
      });
    });
  }
  const previewNodes = buildConcatenatedValue(p.eachField.selectedFields, labelById, p.eachField.seperator) || null;
  return (
    <div className="flex flex-col gap-2" style={{ width: p.width }}>
      <TextFieldComponent value={previewNodes ?? ""} multiline key={p.name} disabled={true} type="text" />
      {previewNodes === null && (
        <div className="flex items-center gap-1 text-sm text-slate-500">
          <span className="flex items-center justify-center">
            <IconComponent className="h-5" name="details" width="1.5rem" height="1.5rem" />
          </span>
          {t("This value is calculated automatically once all required fields are filled")}
        </div>
      )}
    </div>
  );
};
const renderAutoNumberField = (p: QuestionFieldRenderProps) => {
  return (
    <div className="flex flex-col gap-2" style={{ width: p.width }}>
      <TextFieldComponent value={p.currentAnswer?.value ?? ""} key={p.name} disabled={true} type="number" />
    </div>
  );
};
export const fieldRenderers: Record<string, (...args: any[]) => ReactNode> = {
  "single-line": renderSingleLine,
  "multi-line": renderMultiLine,
  "single-select": renderSingleSelect,
  "multi-select": renderMultiSelect,
  "date-time": renderDateTime,
  "look-up": renderLookUp,
  number: renderNumber,
  users: renderUsers,
  fileUpload: renderFileUpload,
  image: renderImage,
  url: renderURL,
  geoLocation: renderGeoLocation,
  formula: renderFormulaField,
  concatenate: renderConcatenateField,
  autoNumber: renderAutoNumberField,
};
export const handleFileOpen = (e: any, data: any, spaceId: any, projectId: any) => {
  e.stopPropagation();
  const { awsKey, fileVersion } = data;
  let url: any = "";
  if (awsKey) {
    url = `${getBackendUrl()}/fileSystemServer/getAttachmentFile/${awsKey}`;
  } else if (fileVersion) {
    url = getViewerUrl({
      fileVersionId: fileVersion,
      spaceId,
      projectId,
    });
  }
  if (url) {
    window.open(url, "_blank");
  }
};

export const getFieldAns = (props: any, currentAnswer: any, handleUrlFieldClick: any, projectId?: any) => {
  const { eachField, spaceId, source, section } = props;
  const {
    fieldType,
    displayAs,
    captureLiveLocation,
    selectedFields,
    seperator,
    displaySize = DEFAULT_IMAGE_DISPLAY_WIDTH,
  }: any = eachField;

  if (["single-select", "number", "single-line", "multi-line", "autoNumber"].includes(fieldType)) {
    return <div className="w-4/5 flex flex-wrap">{currentAnswer.value}</div>;
  } else if (fieldType == "multi-select") {
    return currentAnswer.value?.join(",");
  } else if (fieldType == "date-time") {
    if (displayAs === "dateAndTime") {
      return currentAnswer.value ? convertDate(Number(currentAnswer.value), "dateForTooltip") : "";
    } else {
      return currentAnswer.value ? convertDate(Number(currentAnswer.value), "dateForProfile") : "";
    }
  } else if (fieldType == "look-up") {
    const ansDisplay = currentAnswer.value?.map((ans: any) => ans.name);
    return ansDisplay?.length ? ansDisplay?.join(",") : "";
  } else if (fieldType == "users") {
    return (
      <div className="flex flex-wrap gap-5 pt-5">
        {currentAnswer.value?.map((user: Object) => getUserCardAndChip({ user, p: { ...props, enabled: false } }))}
      </div>
    );
  } else if (fieldType == "fileUpload") {
    return (
      <span className="form-answer-file-list flex flex-col pe-3 overflow-auto gap-4 w-full h-full">
        {currentAnswer.value?.map((data: any, index: number) => (
          <span
            key={`${data.name}-${index}`}
            className="flex gap-3 border-solid border-2 border-button_light w-fit pe-2 rounded-lg ps-2 items-center"
          >
            <IconComponent
              onClick={(e: React.MouseEvent) => handleFileOpen(e, data, spaceId, projectId)}
              name="openInViewer"
              width="1.25rem"
              height="1.25rem"
            />
            <span className="w-14 h-10 flex items-center object-contain">{getFileIcon(data.name)}</span>
            <span>{data.name}</span>
          </span>
        ))}
      </span>
    );
  } else if (fieldType == "image") {
    let answerValue = currentAnswer.value;
    return (
      <div className="form-answer-image-wrap flex flex-wrap flex-row pe-3 overflow-auto gap-4 w-full h-full">
        {answerValue?.map((data: any) => (
          <span
            key={data.awsKey}
            style={{ width: `${displaySize}%` }}
            className="h-fit border-button_light p-1 rounded flex border-2"
          >
            <img
              src={`${getBackendUrl()}/fileSystemServer/getAttachmentFile/${data.awsKey}`}
              alt="logo"
              width="100%"
              className="form-answer-image"
            />
          </span>
        ))}
      </div>
    );
  } else if (fieldType == "url") {
    if (!_.get(currentAnswer, "value.displayText", "")) {
      return "";
    }
    if (displayAs == "button") {
      return (
        <div>
          <ButtonComponent onClick={handleUrlFieldClick} buttonType={BUTTON_TYPES.BORDER_BUTTON}>
            <Text sx={{ maxWidth: "12.5rem" }}>{_.get(currentAnswer, "value.displayText", "")}</Text>
          </ButtonComponent>
        </div>
      );
    } else {
      return (
        <Link
          href={getUrlFromField(_.get(currentAnswer, "value.URL", ""))}
          target="_blank"
          underline="always"
          className="cursor-pointer"
        >
          {_.get(currentAnswer, "value.displayText", "")}
        </Link>
      );
    }
  } else if (fieldType == "geoLocation") {
    const distance = getDistanceInMeters({ pos1: currentAnswer.value?.current, pos2: currentAnswer.value?.selected });
    return currentAnswer.value ? (
      <div className="flex h-80 w-120 border flex-col gap-5 border-button_light p-3 rounded-lg">
        <GoogleMap
          allowManualSelection={false}
          allowPanning={false}
          autoFetchLiveLocation={false}
          showLiveLocation={!!currentAnswer.value?.current}
          zoomLevel={currentAnswer.value?.mapZoomLevel}
          defaultCenter={currentAnswer.value?.current}
          additionalMarker={currentAnswer.value?.selected}
          allowSearch={false}
          getLocationCallBack={undefined}
        />
        {distance > 500 && getOverDistanceWarning()}
        {captureLiveLocation && !currentAnswer.value?.current && getNoPermissionWarning()}
      </div>
    ) : (
      ""
    );
  } else if (fieldType == "formula") {
    const fieldVariableMapper = eachField.fieldsVariableMapper ?? {};
    const formulaExpression = eachField.formulaExpression || "";
    const scope: Record<string, number> = {};
    const isCurrentSectionTable = source === formSectionType.TABLE_SECTION;

    if (isCurrentSectionTable) {
      const rowData = section.tableDataSource?.[props.tableRowIndex] ?? {};
      (section.questionFields ?? []).forEach((question: TemplateQuestionField) => {
        const fieldKey = fieldVariableMapper[question._id];
        const numericValue = toFiniteNumber(rowData?.[question._id]?.value);
        if (fieldKey && numericValue !== null) {
          scope[fieldKey] = numericValue;
        }
      });
    } else {
      (props.formData?.selectedForm?.template ?? []).forEach((section: TemplateSection) => {
        if (section.sectionCode === SECTION_TYPE_TABLE_SECTION) return;
        (section.questionFields ?? []).forEach((question: TemplateQuestionField) => {
          const fieldKey = fieldVariableMapper[question._id];
          const numericValue = toFiniteNumber(question.answer?.value);
          if (fieldKey && numericValue !== null) {
            scope[fieldKey] = numericValue;
          }
        });
      });
    }

    const calculatedValue = evaluateFormula(formulaExpression, scope);
    return calculatedValue;
  } else if (fieldType == "concatenate") {
    const labelById = new Map<string, string>();
    const isCurrentSectionTable = section.sectionCode === SECTION_TYPE_TABLE_SECTION;

    if (isCurrentSectionTable) {
      const rowData = section.tableDataSource?.[props.tableRowIndex] ?? {};
      Object.keys(rowData).forEach((key: string) => {
        labelById.set(key, rowData[key]?.value ?? "");
      });
    } else {
      (props.formData?.selectedForm?.template ?? []).forEach((section: TemplateSection) => {
        if (section.sectionCode === SECTION_TYPE_TABLE_SECTION) return;
        (section.questionFields ?? []).forEach((question: TemplateQuestionField) => {
          labelById.set(question._id, question?.answer?.value ?? "");
        });
      });
    }
    return buildConcatenatedValue(selectedFields, labelById, seperator);
  }
  return "";
};
